import {
  IBaseDataLayer,
  IDataLayerVariable,
} from "@almamedia/amr-adtech-stack/lib/services/dataLayer";

interface ICustomVariables extends IBaseDataLayer {
  destinationURL: IDataLayerVariable<string>;
  validate: IDataLayerVariable<boolean>;
  viewPort: IDataLayerVariable<string>;

  // Kauppalehti
  articleType: IDataLayerVariable<string>;
  locationPathName: IDataLayerVariable<string>;
  pageTitle: IDataLayerVariable<string>;
  pageThemes: IDataLayerVariable<string[]>;
  tilaaja: IDataLayerVariable<boolean>;
  aihesanat: IDataLayerVariable<string[]>;
  contentVisibility: IDataLayerVariable<string>;
}

type DataLayerImplementation = {
  name: string;
  id: string;
  data: ICustomVariables;
};

function getPath(object: Record<string, unknown>, path: string): any {
  return (
    path
      .replace(/\[/g, ".")
      .replace(/\]/g, "")
      .split(".")
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .reduce((o, k) => {
        return (o || {})[k];
      }, object)
  );
}

const dataLayer: DataLayerImplementation = {
  name: "Kauppalehti Data Layer",
  id: "klDatalayer",
  data: {
    viewPort: {
      name: "viewport",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.attributes.width") || "";
      },
    },
    sysEnv: {
      name: "system environment",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "digitalData.page.pageInfo.sysEnv") || "";
      },
    },
    destinationURL: {
      name: "destination url",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.pageInfo.locationHref") || "";
      },
    },
    primaryCategory: {
      name: "primary category",
      get() {
        const digitalData = window?.digitalData;
        return (
          getPath(digitalData, "page.category.primaryCategory") || "etusivu"
        );
      },
    },
    subCategory1: {
      name: "sub category",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.category.subCategory1") || "";
      },
    },
    pageId: {
      // unique name
      name: "page ID", // friendly name
      get() {
        const digitalData = window?.digitalData;
        return digitalData &&
          digitalData.page &&
          digitalData.page.pageInfo &&
          digitalData.page.pageInfo.pageID
          ? digitalData.page.pageInfo.pageID
          : "";
      },
    },
    articleType: {
      // unique name
      name: "article type", // friendly name
      get() {
        const digitalData = window?.digitalData;
        return digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.content &&
          digitalData.page.attributes.content.articleType
          ? digitalData.page.attributes.content.articleType
          : "";
      },
    },
    locationHref: {
      name: "location Href url",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.pageInfo.locationHref") || "error";
      },
    },
    locationPathName: {
      name: "page id",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.pageInfo.locationPathName") || "";
      },
    },
    pageTitle: {
      name: "title",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.pageInfo.pageTitle") || "";
      },
    },
    pageTags: {
      name: "page tags",
      get() {
        const digitalData = window?.digitalData;
        return (getPath(digitalData, "page.category.tags") ?? []).map(function (
          t: any
        ) {
          return t.toLowerCase();
        });
      },
    },
    pageThemes: {
      name: "page themes",
      get() {
        const digitalData = window?.digitalData;
        return (getPath(digitalData, "page.category.themes") || []).map(
          function (t: any) {
            return t.toLowerCase();
          }
        );
      },
    },
    section: {
      name: "section for ad load",
      get() {
        const digitalData = window?.digitalData;
        let section = "noads";
        if (digitalData && digitalData.page) {
          const ngProps = {
            env: getPath(digitalData, "page.attributes.environment") || null,
            sysEnv: getPath(digitalData, "page.pageInfo.sysEnv") || null,
            tags:
              getPath(digitalData, "digitalData.page.category.tags") || null,
            primaryCat:
              getPath(digitalData, "page.category.primaryCategory") || "noads",
            sub1: getPath(digitalData, "page.category.subCategory1"),
            pageId: getPath(digitalData, "page.pageInfo.pageID") || null,
          };
          section = ngProps.primaryCat;

          if (
            section !== "noads" &&
            getPath(digitalData, "page.attributes.adsDisabled") !== true
          ) {
            if (ngProps.pageId === "paywall") {
              section = "tilaa";
            } else if (section === "uutiset" && ngProps.sub1 === "artikkeli") {
              section = "uutiset_artikkeli";
            } else if (section === "kumppanisisaltoa") {
              section = "kaupallinen_yhteistyo";
            } else if (section === "työpaikat") {
              section = "tyopaikat";
            } else if (section !== "yritykset") {
              section = "uutiset";
            }
            if (ngProps.sysEnv === "Android App v2") {
              return `${section}-app`;
            }
            if (window.innerWidth >= 1554) {
              return `${section}_desktop_wide`;
            }
            if (window.innerWidth >= 980) {
              return `${section}_desktop`;
            }
            if (window.innerWidth >= 768) {
              return `${section}_portable`;
            }
            return `${section}_mobile`;
          }
        }
        return section;
      },
    },
    ads: {
      name: "enable / disable ads",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.attributes.ads") !== false;
      },
    },
    validate: {
      name: "validate",
      get() {
        const digitalData = window?.digitalData;
        return getPath(digitalData, "page.category.pageType") === "article";
      },
    },
    pageType: {
      name: "validate",
      get() {
        return window?.digitalData?.page?.category?.pageType;
      },
    },
    accountType: {
      name: "user account type",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.accountType;
      },
    },
    alma_id: {
      name: "alma_id hash",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.almaIDHash;
      },
    },
    amid: {
      name: "alma master id",
      get: (): string | undefined => {
        try {
          return window.localStorage.getItem("gravitoOPUid") ?? undefined;
        } catch (e) {
          return undefined;
        }
      },
    },
    cdime: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdime ?? window?.ALMA?.tunnus?.getUser()?.cdime;
      },
    },
    cdimu: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdimu;
      },
    },
    tilaaja: {
      name: "is user subscribed",
      get: (): boolean => {
        return window?.digitalData?.user[0]?.segment?.credentials?.includes(
          "Digitilaus"
        );
      },
    },
    aihesanat: {
      name: "themes that user is following",
      get: (): string[] => {
        return window?.digitalData?.user[0]?.profile[0]?.profileInfo
          ?.followedTopics;
      },
    },
    contentVisibility: {
      name: "sha256 hash of email",
      get: (): string => {
        return window?.digitalData?.page?.attributes?.contentVisibility;
      },
    },
    issueDate: {
      name: "article issue date",
      get: (): string => {
        return window?.digitalData?.page?.pageInfo?.issueDate ?? "";
      },
    },
    cid: {
      name: "content id",
      get: (): string => {
        return window?.digitalData?.page?.attributes?.content?.cid ?? "";
      },
    },
  },
};

export default dataLayer;
export type { ICustomVariables };
